import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from '../services/api_service';


const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040",
  border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96", dim: "#384660",
  blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981", purple: "#8b5cf6",
  cyan: "#06b6d4", red: "#ef4444", teal: "#14b8a6", orange: "#f97316", pink: "#ec4899",
};

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1";
const api = async (path, opts) => {
  try {
    const r = await fetch(`${API}${path}`, { headers: { "Content-Type": "application/json" }, ...opts, body: opts?.body ? JSON.stringify(opts.body) : undefined });
    return r.ok ? await r.json() : { error: `${r.status} ${r.statusText}` };
  } catch { return { error: "Network error — backend not running. UI is in demo mode." }; }
};

const Pill = ({ color, children }) => <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 600, background: color + "18", color, border: `1px solid ${color}33`, whiteSpace: "nowrap" }}>{children}</span>;
const Stat = ({ label, value, color, sub }) => <div style={{ padding: "12px 14px", borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, flex: "1 1 120px", minWidth: 100 }}><div style={{ fontSize: 9, color: C.muted, letterSpacing: .5, marginBottom: 4, textTransform: "uppercase" }}>{label}</div><div style={{ fontSize: 22, fontWeight: 700, color: color || C.text, lineHeight: 1 }}>{value}</div>{sub && <div style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>{sub}</div>}</div>;
const Bar = ({ pct, color, h }) => <div style={{ height: h || 6, borderRadius: 3, background: C.dim + "33", overflow: "hidden" }}><div style={{ height: "100%", borderRadius: 3, background: color || C.emerald, width: `${Math.min(100, pct)}%`, transition: "width .5s" }} /></div>;
const Btn = ({ children, onClick, color, disabled, small }) => <button onClick={onClick} disabled={disabled} style={{ padding: small ? "4px 10px" : "7px 16px", borderRadius: 6, border: `1px solid ${color || C.blue}`, background: (color || C.blue) + "18", color: color || C.blue, fontSize: small ? 10 : 11, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .5 : 1, transition: "all .15s" }}>{children}</button>;
const Inp = ({ label, value, onChange, type, placeholder }) => <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>{label && <label style={{ fontSize: 10, color: C.muted }}>{label}</label>}<input type={type || "text"} value={value} onChange={e => onChange(type === "number" ? Number(e.target.value) : e.target.value)} placeholder={placeholder} style={{ padding: "7px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12, outline: "none" }} /></div>;
const Sel = ({ label, value, onChange, options, placeholder }) => <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>{label && <label style={{ fontSize: 10, color: C.muted }}>{label}</label>}<select value={value} onChange={e => onChange(e.target.value)} style={{ padding: "7px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 12 }}>{placeholder && <option value="">{placeholder}</option>}{options.map(o => <option key={o} value={o}>{o}</option>)}</select></div>;
const SubTab = ({ tabs, active, onSet }) => <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>{tabs.map(t => <button key={t.id} onClick={() => onSet(t.id)} style={{ padding: "4px 10px", borderRadius: 5, border: `1px solid ${active === t.id ? C.blue : C.border}`, background: active === t.id ? C.blue + "18" : "transparent", color: active === t.id ? C.blue : C.muted, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>{t.l}</button>)}</div>;

const SECTORS = ["oilgas", "mining", "finance", "energy", "pharma", "tech", "transport", "realestate", "food", "apparel", "agriculture", "manufacturing", "chemicals"];
const JURISDICTIONS = ["EU", "UK", "US", "AU", "SG", "JP", "HK", "BR", "CA", "CH", "NZ"];
const VCP = ["manufacturer", "importer", "service_provider", "financial_intermediary", "retailer"];
const PRI_COL = { critical: C.red, high: C.amber, medium: C.blue, low: C.dim };

// ═══════════════════════════════════════════════════════════
// TAB 1: ENTITY
// ═══════════════════════════════════════════════════════════
function EntityTab() {
  const [f, setF] = useState({ entity_id: "ent_001", name: "", jurisdiction: "EU", sector: "manufacturing", nace_codes: "C24", employee_count: 5000, revenue_eur: 2000000000, listed: true, value_chain_position: "manufacturer" });
  const [res, setRes] = useState(null);
  const [fws, setFws] = useState(null);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const go = async () => {
    const d = await api("/entity/profile", { method: "POST", body: { ...f, nace_codes: f.nace_codes.split(",").map(s => s.trim()) } });
    setRes(d); setFws(await api("/entity/frameworks"));
  };
  return <div>
    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Entity profile</div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
      <Inp label="Company name" value={f.name} onChange={v => set("name", v)} placeholder="Stahl AG" />
      <Sel label="Jurisdiction" value={f.jurisdiction} onChange={v => set("jurisdiction", v)} options={JURISDICTIONS} />
      <Sel label="Sector" value={f.sector} onChange={v => set("sector", v)} options={SECTORS} />
      <Sel label="Value chain" value={f.value_chain_position} onChange={v => set("value_chain_position", v)} options={VCP} />
      <Inp label="NACE codes" value={f.nace_codes} onChange={v => set("nace_codes", v)} placeholder="C24, C25" />
      <Inp label="Employees" value={f.employee_count} onChange={v => set("employee_count", v)} type="number" />
      <Inp label="Revenue (EUR)" value={f.revenue_eur} onChange={v => set("revenue_eur", v)} type="number" />
      <label style={{ display: "flex", alignItems: "end", gap: 6, fontSize: 11, color: C.text, cursor: "pointer", paddingBottom: 6 }}>
        <input type="checkbox" checked={f.listed} onChange={e => set("listed", e.target.checked)} /> Listed
      </label>
    </div>
    <Btn onClick={go} disabled={!f.name}>Detect frameworks</Btn>
    {res && !res.error && <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <Stat label="Mandatory" value={res.mandatory?.length || 0} color={C.red} />
        <Stat label="Conditional" value={res.conditional?.length || 0} color={C.amber} />
        <Stat label="Voluntary" value={res.voluntary_available?.length || 0} color={C.blue} />
      </div>
      {fws?.frameworks?.map(fw => <div key={fw.framework_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 5, background: C.card, border: `1px solid ${C.border}`, marginBottom: 3 }}>
        <Pill color={fw.applicability === "mandatory" ? C.red : fw.applicability === "conditional" ? C.amber : fw.applicability === "voluntary" ? C.blue : C.dim}>{fw.applicability}</Pill>
        <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{fw.framework_id}</span>
        <span style={{ fontSize: 9, color: C.muted, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fw.reason}</span>
      </div>)}
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 2: IRO MATRIX (Phase 5 — G15)
// ═══════════════════════════════════════════════════════════
function IROTab() {
  const [ind, setInd] = useState("");
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null);
  const run = async () => { const r = await api("/iro/assess", { method: "POST", body: { industry: ind } }); setData(r); };
  const load = async () => { const r = await api("/iro/matrix"); if (r?.assessments?.length > 0) setData(r); };
  useEffect(() => { load(); }, []);

  const DEMO = [
    { esrs_topic: "E1", topic_name: "Climate change", impact_score: 4.05, financial_score: 3.8, verdict: "material_significant", double_materiality: true, iro_type: "impact" },
    { esrs_topic: "E2", topic_name: "Pollution", impact_score: 3.05, financial_score: 2.8, verdict: "material", double_materiality: false, iro_type: "risk" },
    { esrs_topic: "E3", topic_name: "Water & marine", impact_score: 2.6, financial_score: 2.3, verdict: "not_material", double_materiality: false, iro_type: "risk" },
    { esrs_topic: "E4", topic_name: "Biodiversity", impact_score: 3.0, financial_score: 2.3, verdict: "material", double_materiality: false, iro_type: "impact" },
    { esrs_topic: "E5", topic_name: "Circular economy", impact_score: 2.8, financial_score: 3.3, verdict: "material", double_materiality: false, iro_type: "opportunity" },
    { esrs_topic: "S1", topic_name: "Own workforce", impact_score: 3.15, financial_score: 3.0, verdict: "material_significant", double_materiality: true, iro_type: "risk" },
    { esrs_topic: "S2", topic_name: "Value chain workers", impact_score: 3.0, financial_score: 2.3, verdict: "material", double_materiality: false, iro_type: "risk" },
    { esrs_topic: "S3", topic_name: "Communities", impact_score: 1.85, financial_score: 1.5, verdict: "not_material", double_materiality: false, iro_type: "impact" },
    { esrs_topic: "S4", topic_name: "Consumers", impact_score: 1.35, financial_score: 1.0, verdict: "not_material", double_materiality: false, iro_type: "impact" },
    { esrs_topic: "G1", topic_name: "Business conduct", impact_score: 3.35, financial_score: 3.8, verdict: "material_significant", double_materiality: true, iro_type: "risk" },
  ];
  const items = data?.assessments || DEMO;
  const W = 420, H = 320, P = 50, PB = 30;
  const scale = (v, min, max, range) => P + ((v - min) / (max - min)) * range;
  const xRange = W - P - PB, yRange = H - P - PB;
  const topicColor = { E: C.teal, S: C.amber, G: C.purple };

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>Double materiality — IRO matrix</div>
      <div style={{ display: "flex", gap: 6 }}>
        <Sel value={ind} onChange={setInd} options={SECTORS} placeholder="Industry..." />
        <Btn onClick={run} disabled={!ind} small color={C.teal}>Assess</Btn>
      </div>
    </div>

    {/* Scatter plot */}
    <div style={{ background: C.card, borderRadius: 10, border: `1px solid ${C.border}`, padding: "16px 12px 8px", marginBottom: 12 }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", margin: "0 auto" }}>
        {/* Quadrant backgrounds */}
        <rect x={P} y={PB} width={scale(3, 0, 5, xRange) - P} height={H - P - PB - (H - P - PB - (scale(3, 0, 5, yRange)))} fill={C.dim + "08"} />
        <rect x={scale(3, 0, 5, xRange)} y={PB} width={W - PB - scale(3, 0, 5, xRange)} height={H - P - PB - (H - P - PB - (scale(3, 0, 5, yRange)))} fill={C.amber + "08"} />
        <rect x={P} y={H - P - (scale(3, 0, 5, yRange))} width={scale(3, 0, 5, xRange) - P} height={scale(3, 0, 5, yRange)} fill={C.blue + "08"} />
        <rect x={scale(3, 0, 5, xRange)} y={H - P - (scale(3, 0, 5, yRange))} width={W - PB - scale(3, 0, 5, xRange)} height={scale(3, 0, 5, yRange)} fill={C.emerald + "08"} />

        {/* Threshold lines */}
        <line x1={scale(3, 0, 5, xRange)} y1={PB} x2={scale(3, 0, 5, xRange)} y2={H - P} stroke={C.red + "44"} strokeDasharray="4 3" />
        <line x1={P} y1={H - P - (scale(3, 0, 5, yRange))} x2={W - PB} y2={H - P - (scale(3, 0, 5, yRange))} stroke={C.red + "44"} strokeDasharray="4 3" />

        {/* Axes */}
        <line x1={P} y1={H - P} x2={W - PB} y2={H - P} stroke={C.dim} strokeWidth={1} />
        <line x1={P} y1={PB} x2={P} y2={H - P} stroke={C.dim} strokeWidth={1} />
        <text x={W / 2} y={H - 6} textAnchor="middle" fill={C.muted} fontSize={10}>Impact score (inside-out)</text>
        <text x={12} y={H / 2} textAnchor="middle" fill={C.muted} fontSize={10} transform={`rotate(-90, 12, ${H / 2})`}>Financial score (outside-in)</text>

        {/* Tick marks */}
        {[0, 1, 2, 3, 4, 5].map(v => <g key={v}>
          <text x={scale(v, 0, 5, xRange)} y={H - P + 14} textAnchor="middle" fill={C.dim} fontSize={9}>{v}</text>
          <text x={P - 8} y={H - P - (v / 5 * yRange) + 3} textAnchor="end" fill={C.dim} fontSize={9}>{v}</text>
        </g>)}

        {/* Quadrant labels */}
        <text x={P + 15} y={H - P - 8} fill={C.dim} fontSize={8}>Not material</text>
        <text x={W - PB - 5} y={PB + 14} textAnchor="end" fill={C.emerald + "88"} fontSize={8} fontWeight={600}>Double material</text>
        <text x={P + 15} y={PB + 14} fill={C.blue + "66"} fontSize={8}>Financial only</text>
        <text x={W - PB - 5} y={H - P - 8} textAnchor="end" fill={C.amber + "66"} fontSize={8}>Impact only</text>

        {/* Data points */}
        {items.map((d, i) => {
          const cx = scale(d.impact_score, 0, 5, xRange);
          const cy = H - P - (d.financial_score / 5 * yRange);
          const col = topicColor[d.esrs_topic[0]] || C.blue;
          const isHov = hover === i;
          const r = d.double_materiality ? 14 : d.verdict.includes("material") ? 11 : 8;
          return <g key={d.esrs_topic} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: "pointer" }}>
            <circle cx={cx} cy={cy} r={r + 3} fill={col + "18"} stroke={col + "44"} strokeWidth={isHov ? 2 : 0} />
            <circle cx={cx} cy={cy} r={isHov ? r + 2 : r} fill={col + (d.double_materiality ? "cc" : "88")} stroke={col} strokeWidth={d.double_materiality ? 2 : 1} style={{ transition: "r .15s" }} />
            <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize={8} fontWeight={700}>{d.esrs_topic}</text>
            {isHov && <g>
              <rect x={cx + r + 6} y={cy - 22} width={140} height={40} rx={4} fill={C.bg + "ee"} stroke={col + "66"} />
              <text x={cx + r + 12} y={cy - 8} fill={C.text} fontSize={10} fontWeight={600}>{d.topic_name}</text>
              <text x={cx + r + 12} y={cy + 6} fill={C.muted} fontSize={9}>I:{d.impact_score.toFixed(1)} F:{d.financial_score.toFixed(1)} {d.iro_type}</text>
            </g>}
          </g>;
        })}
      </svg>
    </div>

    {/* Legend + summary */}
    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
      <Stat label="Material" value={items.filter(i => i.verdict.includes("material")).length} color={C.amber} />
      <Stat label="Double" value={items.filter(i => i.double_materiality).length} color={C.emerald} />
      <Stat label="Not material" value={items.filter(i => !i.verdict.includes("material")).length} color={C.dim} />
    </div>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {items.map(d => <div key={d.esrs_topic} style={{ padding: "4px 8px", borderRadius: 5, background: C.card, border: `1px solid ${d.double_materiality ? C.emerald + "44" : d.verdict.includes("material") ? C.amber + "33" : C.dim + "22"}`, opacity: d.verdict.includes("material") ? 1 : .5, fontSize: 10 }}>
        <Pill color={topicColor[d.esrs_topic[0]] || C.blue}>{d.esrs_topic}</Pill>
        <span style={{ marginLeft: 4, color: C.text }}>{d.topic_name}</span>
        {d.double_materiality && <span style={{ marginLeft: 4, color: C.emerald, fontSize: 9, fontWeight: 700 }}>DOUBLE</span>}
      </div>)}
    </div>
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 3: OBSERVABILITY DASHBOARD (Phase 5)
// ═══════════════════════════════════════════════════════════
function ObservabilityTab() {
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [dash, setDash] = useState(null);
  const [sub, setSub] = useState("overview");

  const load = async () => {
    await api("/observability/metrics/capture", { method: "POST" });
    setMetrics(await api("/observability/metrics/latest"));
    setAlerts(await api("/observability/alerts"));
    setDash(await api("/observability/dashboard"));
  };
  useEffect(() => { load(); }, []);

  const m = metrics?.metrics || {};
  const sev = alerts?.by_severity || {};
  const aList = alerts?.alerts || [];

  // Demo data for when backend isn't running
  const demoMetrics = { compliance_pct: { value: 12.5 }, evidence_coverage: { value: 34.7 }, claim_coverage: { value: 8.3 }, open_gaps: { value: 252 }, chain_integrity: { value: 1.0 }, ai_draft_acceptance: { value: 66.7 } };
  const demoAlerts = [
    { alert_id: "alrt_001", alert_type: "deadline_proximity", severity: "critical", title: "CSRD/ESRS filing deadline in 28 days", message: "FY2024 filing due 2025-04-30", status: "open", created_at: new Date().toISOString() },
    { alert_id: "alrt_002", alert_type: "compliance_drop", severity: "critical", title: "CBAM compliance below 10%", message: "24 open requirements", status: "open", created_at: new Date().toISOString() },
    { alert_id: "alrt_003", alert_type: "evidence_gap", severity: "warning", title: "83% of requirements lack evidence", message: "252 of 288 open", status: "open", created_at: new Date().toISOString() },
  ];
  const displayMetrics = Object.keys(m).length > 0 ? m : demoMetrics;
  const displayAlerts = aList.length > 0 ? aList : demoAlerts;

  const sevCol = { critical: C.red, warning: C.amber, info: C.blue };

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>Observability</div>
      <Btn onClick={load} small color={C.teal}>Refresh</Btn>
    </div>
    <SubTab tabs={[{ id: "overview", l: "Overview" }, { id: "alerts", l: "Alerts" }, { id: "security", l: "Security" }]} active={sub} onSet={setSub} />

    {sub === "overview" && <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <Stat label="Compliance" value={`${(displayMetrics.compliance_pct?.value ?? displayMetrics.compliance_pct ?? 0).toFixed(1)}%`} color={C.cyan} />
        <Stat label="Evidence coverage" value={`${(displayMetrics.evidence_coverage?.value ?? displayMetrics.evidence_coverage ?? 0).toFixed(1)}%`} color={C.blue} />
        <Stat label="Open gaps" value={displayMetrics.open_gaps?.value ?? displayMetrics.open_gaps ?? 0} color={C.red} />
        <Stat label="Chain" value={Number(displayMetrics.chain_integrity?.value ?? displayMetrics.chain_integrity ?? 1) === 1 ? "VALID" : "BROKEN"} color={Number(displayMetrics.chain_integrity?.value ?? 1) === 1 ? C.emerald : C.red} />
      </div>

      {/* Compliance gauge */}
      <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, padding: 14, marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Overall compliance</div>
        <Bar pct={displayMetrics.compliance_pct?.value ?? displayMetrics.compliance_pct ?? 0} color={C.emerald} h={12} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 9, color: C.dim }}>0%</span>
          <span style={{ fontSize: 10, fontWeight: 600, color: C.emerald }}>{(displayMetrics.compliance_pct?.value ?? displayMetrics.compliance_pct ?? 0).toFixed(1)}%</span>
          <span style={{ fontSize: 9, color: C.dim }}>100%</span>
        </div>
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Health status: <span style={{ color: dash?.health === "healthy" ? C.emerald : C.red }}>{dash?.health || "at_risk"}</span></div>
      <div style={{ display: "flex", gap: 6 }}>
        <Stat label="Open alerts" value={dash?.open_alerts ?? displayAlerts.length} color={C.red} sub={`crit: ${sev.critical ?? displayAlerts.filter(a => a.severity === "critical").length}`} />
        <Stat label="SLA policies" value={dash?.active_sla_policies ?? 0} color={C.purple} />
      </div>
    </div>}

    {sub === "alerts" && <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <Btn onClick={async () => { await api("/observability/alerts/check", { method: "POST" }); load(); }} small color={C.purple}>Run alert check</Btn>
        <div style={{ fontSize: 9, color: C.dim, alignSelf: "center" }}>Evaluates SLA policies and compliance thresholds</div>
      </div>
      {displayAlerts.map(a => <div key={a.alert_id} style={{ padding: "8px 10px", borderRadius: 6, background: C.card, border: `1px solid ${(sevCol[a.severity] || C.dim) + "33"}`, marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <Pill color={sevCol[a.severity] || C.dim}>{a.severity}</Pill>
          <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{a.title}</span>
          <Pill color={a.status === "open" ? C.red : a.status === "acknowledged" ? C.amber : C.emerald}>{a.status}</Pill>
          {a.status === "open" && <Btn onClick={async () => { await api(`/observability/alerts/${a.alert_id}/acknowledge`, { method: "PATCH" }); load(); }} small color={C.amber}>Ack</Btn>}
          {(a.status === "open" || a.status === "acknowledged") && <Btn onClick={async () => { await api(`/observability/alerts/${a.alert_id}/resolve`, { method: "PATCH" }); load(); }} small color={C.emerald}>Resolve</Btn>}
        </div>
        <div style={{ fontSize: 10, color: C.muted }}>{a.message}</div>
        <div style={{ fontSize: 9, color: C.dim, marginTop: 2 }}>{a.alert_type} | {a.created_at}</div>
      </div>)}
    </div>}

    {sub === "security" && <SecuritySub />}
  </div>;
}

function SecuritySub() {
  const [logs, setLogs] = useState(null);
  const [summary, setSummary] = useState(null);
  const load = async () => { setLogs(await api("/security/logs?limit=30")); setSummary(await api("/security/summary")); };
  useEffect(() => { load(); }, []);
  const s = summary || {};
  return <div>
    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      <Stat label="Security score" value={`${s.security_score ?? 100}%`} color={(s.security_score ?? 100) >= 95 ? C.emerald : C.amber} />
      <Stat label="Total events" value={s.total ?? 0} />
      <Stat label="Failures" value={s.failures ?? 0} color={(s.failures ?? 0) > 0 ? C.red : C.emerald} />
      <Stat label="Denials" value={s.denials ?? 0} color={(s.denials ?? 0) > 0 ? C.amber : C.emerald} />
    </div>
    {s.by_type && <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Event types</div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{Object.entries(s.by_type || {}).map(([t, c]) => <Pill key={t} color={C.cyan}>{t}: {c}</Pill>)}</div>
    </div>}
    <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Recent security events <Btn onClick={load} small color={C.teal}>Refresh</Btn></div>
    {logs?.logs?.map((l, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 4, background: C.card, marginBottom: 2, fontSize: 10 }}>
      <Pill color={l.severity === "high" ? C.red : l.severity === "medium" ? C.amber : C.blue}>{l.event_type}</Pill>
      <span style={{ color: C.muted, flex: 1 }}>{l.action} {l.resource_type ? `on ${l.resource_type}` : ""}</span>
      <Pill color={l.outcome === "success" ? C.emerald : C.red}>{l.outcome}</Pill>
      <span style={{ fontSize: 8, color: C.dim }}>{l.actor_id}</span>
    </div>)}
    {(!logs?.logs || logs.logs.length === 0) && <div style={{ fontSize: 11, color: C.muted, textAlign: "center", padding: 16 }}>Security events populate as users interact with the system</div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 4: AI DRAFTS — REAL LLM INTEGRATION
// ═══════════════════════════════════════════════════════════
function AITab() {
  const [drafts, setDrafts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [reqIds, setReqIds] = useState("");
  const [evIds, setEvIds] = useState("");
  const [evContext, setEvContext] = useState("Stahl AG reported 127,450 tCO2e of Scope 1 GHG emissions from stationary combustion in FY2024.");
  const [result, setResult] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [sub, setSub] = useState("generate");

  const loadDrafts = async () => {
    const r = await api("/drafts/claims"); setDrafts(r?.drafts || []);
    const a = await api("/drafts/audit-log?limit=20"); setAuditLog(a?.invocations || []);
  };
  useEffect(() => { loadDrafts(); }, []);

  const generate = async () => {
    setGenerating(true); setResult(null);
    try {
      const body = { requirement_ids: reqIds ? reqIds.split(",").map(s => s.trim()) : [],
                      evidence_ids: evIds ? evIds.split(",").map(s => s.trim()) : [],
                      evidence_context: evContext };
      const r = await api("/drafts/claims/generate", { method: "POST", body });
      setResult(r);
      loadDrafts();
    } catch (e) {
      setResult({ error: e.message, claim_text: "Backend AI call failed" });
    }
    setGenerating(false);
  };

  const promote = async (id) => {
    await api(`/drafts/claims/${id}/promote`, { method: "POST" });
    loadDrafts();
  };
  const reject = async (id) => {
    await api(`/drafts/claims/${id}/reject`, { method: "POST" });
    loadDrafts();
  };

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>AI-powered claim drafting <Pill color={C.purple}>{result?.live_llm ? "Live LLM" : "Backend"}</Pill></div>
      <Btn onClick={loadDrafts} small color={C.teal}>Refresh</Btn>
    </div>
    <SubTab tabs={[{ id: "generate", l: "Generate" }, { id: "drafts", l: `Drafts (${drafts.length})` }, { id: "audit", l: "Audit log" }]} active={sub} onSet={setSub} />

    {sub === "generate" && <div>
      <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.purple}33`, padding: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>Backend AI pipeline: /api/v1/drafts/claims/generate</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <Inp label="Requirement IDs (comma-sep)" value={reqIds} onChange={setReqIds} placeholder="req_abc, req_def" />
          <Inp label="Evidence IDs (comma-sep)" value={evIds} onChange={setEvIds} placeholder="ev_123 (optional — auto-discovers)" />
        </div>
        <div style={{ marginBottom: 8 }}><Inp label="Evidence context" value={evContext} onChange={setEvContext} placeholder="Describe the evidence text..." /></div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn onClick={generate} disabled={generating} color={C.purple}>{generating ? "Generating..." : "Generate via backend"}</Btn>
          <div style={{ fontSize: 9, color: C.dim, alignSelf: "center" }}>Routes through backend AI pipeline → isolated DraftClaim store</div>
        </div>
      </div>

      {result && !result.error && <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.emerald}33`, padding: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600 }}>AI draft output</span>
          <Pill color={C.purple}>conf: {((result.confidence || result.ai_confidence || 0) * 100).toFixed(0)}%</Pill>
          <Pill color={C.teal}>{result.model_version || "backend"}</Pill>
          <Pill color={result.live_llm ? C.emerald : C.amber}>{result.live_llm ? "Live LLM" : "Simulated"}</Pill>
        </div>
        <div style={{ fontSize: 11, color: C.text, lineHeight: 1.5, marginBottom: 8, padding: 8, background: C.surface, borderRadius: 6 }}>{result.claim_text}</div>
        <div style={{ display: "flex", gap: 8, fontSize: 10, color: C.muted }}>
          <span>Draft: {result.draft_claim_id}</span>
          <span>Invocation: {result.ai_invocation_id}</span>
        </div>
      </div>}
      {result?.error && <div style={{ padding: 10, borderRadius: 6, background: C.red + "18", border: `1px solid ${C.red}33`, marginBottom: 12, fontSize: 11, color: C.red }}>{result.error}: {result.claim_text}</div>}
    </div>}

    {sub === "drafts" && <div>
      {drafts.map(d => <div key={d.draft_claim_id} style={{ padding: 8, borderRadius: 6, background: C.card, border: `1px solid ${d.status === "pending_review" ? C.amber : d.status === "accepted" ? C.emerald : C.red}22`, marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <Pill color={d.status === "pending_review" ? C.amber : d.status === "accepted" ? C.emerald : C.red}>{d.status}</Pill>
          <span style={{ fontSize: 9, color: C.dim, fontFamily: "monospace" }}>{d.draft_claim_id}</span>
          <Pill color={C.purple}>conf: {((d.ai_confidence || 0) * 100).toFixed(0)}%</Pill>
          <span style={{ flex: 1 }} />
          {d.status === "pending_review" && <>
            <Btn onClick={() => promote(d.draft_claim_id)} small color={C.emerald}>Promote</Btn>
            <Btn onClick={() => reject(d.draft_claim_id)} small color={C.red}>Reject</Btn>
          </>}
        </div>
        <div style={{ fontSize: 10, color: C.text, lineHeight: 1.4 }}>{(d.claim_text || "").substring(0, 200)}</div>
        <div style={{ fontSize: 9, color: C.dim, marginTop: 4 }}>
          Reqs: {(d.requirement_ids || []).length} | Evidence: {(d.evidence_ids || []).length} | {d.created_at}
        </div>
      </div>)}
      {drafts.length === 0 && <div style={{ fontSize: 11, color: C.muted, padding: 20, textAlign: "center" }}>No drafts yet — generate one above</div>}
    </div>}

    {sub === "audit" && <div>
      {auditLog.map(a => <div key={a.invocation_id} style={{ padding: 6, borderRadius: 4, background: C.card, border: `1px solid ${C.border}`, marginBottom: 2, fontSize: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontFamily: "monospace", color: C.dim, width: 100, overflow: "hidden", textOverflow: "ellipsis" }}>{a.invocation_id}</span>
          <Pill color={C.purple}>{a.prompt_template_id}</Pill>
          <Pill color={C.teal}>{a.model_version}</Pill>
          <Pill color={C.blue}>conf: {((a.confidence || 0) * 100).toFixed(0)}%</Pill>
          <span style={{ flex: 1 }} />
          <span style={{ color: C.dim }}>{a.invoked_at}</span>
        </div>
      </div>)}
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 5: ENTERPRISE (Phase 6)
// ═══════════════════════════════════════════════════════════
function EnterpriseTab() {
  const [entities, setEntities] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [exports, setExports] = useState(null);
  const [backups, setBackups] = useState(null);
  const [sub, setSub] = useState("multi");

  useEffect(() => {
    (async () => {
      setEntities(await api("/enterprise/entities"));
      setAnalytics(await api("/enterprise/analytics"));
      setExports(await api("/enterprise/exports"));
      setBackups(await api("/enterprise/backups"));
    })();
  }, []);

  const runExport = async () => { await api("/enterprise/export", { method: "POST", body: { export_type: "xbrl", framework: "CSRD/ESRS" } }); setExports(await api("/enterprise/exports")); };
  const runBackup = async () => { await api("/enterprise/backup", { method: "POST" }); setBackups(await api("/enterprise/backups")); };

  const a = analytics || {};
  const ents = entities?.entities || [];
  const exps = exports?.exports || [];
  const baks = backups?.backups || [];

  return <div>
    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Enterprise</div>
    <SubTab tabs={[{ id: "multi", l: "Multi-entity" }, { id: "export", l: "XBRL export" }, { id: "backup", l: "Backup/DR" }]} active={sub} onSet={setSub} />

    {sub === "multi" && <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Stat label="Entities" value={a.total_entities ?? ents.length} />
        <Stat label="Overall compliance" value={`${a.overall_compliance_pct ?? 0}%`} color={C.cyan} />
        <Stat label="Requirements" value={a.total_requirements ?? 0} />
      </div>
      {ents.map(e => <div key={e.entity_id} style={{ padding: 8, borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, marginBottom: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{e.name}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: e.compliance_pct >= 50 ? C.emerald : C.amber }}>{e.compliance_pct}%</span>
        </div>
        <Bar pct={e.compliance_pct} color={e.compliance_pct >= 50 ? C.emerald : C.amber} />
        <div style={{ display: "flex", gap: 8, marginTop: 4, fontSize: 9, color: C.muted }}>
          <span>{e.jurisdiction}</span><span>{e.sector}</span><span>{e.frameworks} FWs</span><span>{e.requirements} reqs</span>
        </div>
      </div>)}
      {a.by_jurisdiction && <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>By jurisdiction</div>
        {Object.entries(a.by_jurisdiction).map(([j, d]) => <div key={j} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
          <Pill color={C.blue}>{j}</Pill>
          <span style={{ fontSize: 10, flex: 1 }}>{d.entities} entities, {d.requirements} reqs</span>
          <span style={{ fontSize: 10, fontWeight: 600, color: d.compliance_pct >= 50 ? C.emerald : C.amber }}>{d.compliance_pct}%</span>
        </div>)}
      </div>}
    </div>}

    {sub === "export" && <div>
      <Btn onClick={runExport} color={C.orange}>Generate XBRL export</Btn>
      <div style={{ marginTop: 12 }}>
        {exps.map(e => <div key={e.export_id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 4, background: C.card, marginBottom: 3 }}>
          <Pill color={e.status === "completed" ? C.emerald : C.amber}>{e.status}</Pill>
          <span style={{ fontSize: 10, flex: 1 }}>{e.export_id}</span>
          <span style={{ fontSize: 9, color: C.muted }}>{e.type} | {e.record_count} records</span>
        </div>)}
      </div>
    </div>}

    {sub === "backup" && <div>
      <Btn onClick={runBackup} color={C.teal}>Run backup</Btn>
      <div style={{ marginTop: 12 }}>
        {baks.map((b, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 4, background: C.card, marginBottom: 3 }}>
          <Pill color={C.emerald}>complete</Pill>
          <span style={{ fontSize: 10, flex: 1 }}>{b.backup_id || b.id || `bak_${i}`}</span>
          <span style={{ fontSize: 9, color: C.muted }}>{b.total_records || "?"} records</span>
        </div>)}
      </div>
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 6: REQUIREMENTS
// ═══════════════════════════════════════════════════════════
function RequirementsTab() {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [byFw, setByFw] = useState(null);
  const [sub, setSub] = useState("overview");
  const load = async () => { setData(await api("/requirements/dashboard")); setReport(await api("/requirements/compliance-report")); setByFw(await api("/requirements/by-framework")); };
  useEffect(() => { load(); }, []);
  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>Requirements</div>
      <div style={{ display: "flex", gap: 4 }}><Btn onClick={async () => { await api("/requirements/generate", { method: "POST" }); load(); }} small>Generate</Btn><Btn onClick={load} small color={C.teal}>Refresh</Btn></div>
    </div>
    <SubTab tabs={[{ id: "overview", l: "Overview" }, { id: "frameworks", l: "By framework" }, { id: "gaps", l: "Gaps" }]} active={sub} onSet={setSub} />
    {sub === "overview" && data && <div>
      {report?.overall && <div style={{ padding: 12, borderRadius: 8, background: C.surface, border: `1px solid ${C.border}`, marginBottom: 12 }}><div style={{ fontSize: 12, marginBottom: 6 }}>{report.overall.statement}</div><Bar pct={report.overall.compliance_pct} color={C.emerald} h={8} /><div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{report.overall.compliance_pct}%</div></div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}><Stat label="Total" value={data.total_requirements} /><Stat label="Satisfied" value={data.satisfied} color={C.emerald} /><Stat label="Open" value={data.open} color={C.red} /></div>
    </div>}
    {sub === "frameworks" && byFw?.frameworks && Object.entries(byFw.frameworks).sort((a, b) => b[1].total - a[1].total).map(([fw, d]) => <div key={fw} style={{ padding: 8, borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, marginBottom: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{ fontSize: 12, fontWeight: 600 }}>{fw}</span><span style={{ fontSize: 14, fontWeight: 700, color: d.compliance_pct >= 50 ? C.emerald : C.amber }}>{d.compliance_pct}%</span></div>
      <Bar pct={d.compliance_pct} color={d.compliance_pct >= 50 ? C.emerald : C.amber} />
      <div style={{ fontSize: 9, color: C.muted, marginTop: 3 }}>Total: {d.total} | Open: {d.open}</div>
    </div>)}
    {sub === "gaps" && report?.top_gaps?.slice(0, 15).map((g, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 4, background: C.card, marginBottom: 3 }}>
      <Pill color={PRI_COL[g.priority]}>{g.priority}</Pill>
      <span style={{ fontSize: 10, flex: 1 }}>{g.topic}</span>
      <Pill color={C.blue}>{g.framework}</Pill>
    </div>)}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 7: EVIDENCE
// ═══════════════════════════════════════════════════════════
function EvidenceTab() {
  const [list, setList] = useState(null);
  const [name, setName] = useState("scope1_emissions.pdf");
  const [topic, setTopic] = useState("Climate Change");
  const load = async () => { setList(await api("/evidence/")); };
  useEffect(() => { load(); }, []);
  const upload = async () => { await api("/evidence/upload-linked", { method: "POST", body: { name, type: "document", source: "Operations/ERP", topic, uploadedBy: "user" } }); load(); };
  return <div>
    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Evidence vault</div>
    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      <Inp label="Filename" value={name} onChange={setName} />
      <Inp label="Topic" value={topic} onChange={setTopic} />
      <div style={{ alignSelf: "end" }}><Btn onClick={upload}>Upload + auto-link</Btn></div>
    </div>
    {list?.evidence?.map(e => <div key={e.evidence_id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 4, background: C.card, border: `1px solid ${C.border}`, marginBottom: 3 }}>
      <Pill color={e.verified ? C.emerald : C.muted}>{e.verified ? "verified" : e.approval_status}</Pill>
      <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{e.name}</span>
      <span style={{ fontSize: 9, color: C.dim }}>{e.content_hash?.substring(0, 12)}</span>
      <Pill color={C.cyan}>{e.requirement_ids?.length || 0} reqs</Pill>
    </div>)}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 8: EVENTS
// ═══════════════════════════════════════════════════════════
function EventsTab() {
  const [stats, setStats] = useState(null);
  const [chain, setChain] = useState(null);
  const [events, setEvents] = useState(null);
  const load = async () => { setStats(await api("/events/stats")); setChain(await api("/events/verify-chain")); setEvents(await api("/events/?limit=20")); };
  useEffect(() => { load(); }, []);
  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>Event store</div>
      <Btn onClick={load} small color={C.teal}>Refresh</Btn>
    </div>
    {stats && <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
      <Stat label="Events" value={stats.total_events} color={C.cyan} />
      <Stat label="Chain" value={chain?.valid ? "VALID" : "BROKEN"} color={chain?.valid ? C.emerald : C.red} />
      <Stat label="Verified" value={chain?.verified || 0} color={C.emerald} />
    </div>}
    {stats?.by_type && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>{Object.entries(stats.by_type).sort((a, b) => b[1] - a[1]).map(([t, c]) => <Pill key={t} color={C.cyan}>{t}: {c}</Pill>)}</div>}
    {events?.events?.map(e => <div key={e.event_id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 4, background: C.card, border: `1px solid ${C.border}`, marginBottom: 2, fontSize: 10 }}>
      <span style={{ color: C.dim, fontFamily: "monospace", width: 90, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{e.event_id}</span>
      <Pill color={C.cyan}>{e.event_type}</Pill>
      <span style={{ color: C.muted, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.aggregate_id}</span>
      <span style={{ color: C.dim, fontFamily: "monospace", fontSize: 8 }}>{e.chain_hash?.substring(0, 12)}</span>
    </div>)}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 9: SEARCH
// ═══════════════════════════════════════════════════════════
function SearchTab() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [semResults, setSemResults] = useState(null);
  const [embStats, setEmbStats] = useState(null);
  const [mode, setSearchMode] = useState("keyword");
  const [sub, setSub] = useState("search");

  const load = async () => { setEmbStats(await api("/semantic/stats")); };
  useEffect(() => { load(); }, []);

  const search = async () => {
    if (!q) return;
    if (mode === "keyword") {
      setResults(await api(`/search?q=${encodeURIComponent(q)}`));
      setSemResults(null);
    } else {
      setSemResults(await api(`/semantic/search?q=${encodeURIComponent(q)}`));
      setResults(null);
    }
  };
  const reindex = async () => { await api("/search/reindex", { method: "POST" }); };
  const buildEmb = async () => { await api("/semantic/build", { method: "POST" }); load(); };
  const reembed = async () => { await api("/semantic/reembed", { method: "POST" }); load(); };

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>Search + semantic matching</div>
      <div style={{ display: "flex", gap: 4 }}>
        <Btn onClick={reindex} small color={C.teal}>Reindex</Btn>
        <Btn onClick={buildEmb} small color={C.purple}>Build embeddings</Btn>
      </div>
    </div>
    <SubTab tabs={[{ id: "search", l: "Search" }, { id: "embeddings", l: "Embeddings" }]} active={sub} onSet={setSub} />

    {sub === "search" && <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
        {[{ id: "keyword", l: "Keyword (full-text)", c: C.blue }, { id: "semantic", l: "Semantic (MiniLM/TF-IDF)", c: C.purple }].map(m =>
          <button key={m.id} onClick={() => setSearchMode(m.id)} style={{ padding: "3px 10px", borderRadius: 5, border: `1px solid ${mode === m.id ? m.c : C.border}`, background: mode === m.id ? m.c + "18" : "transparent", color: mode === m.id ? m.c : C.muted, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>{m.l}</button>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}><Inp value={q} onChange={setQ} placeholder={mode === "keyword" ? "Search by keywords..." : "Search by meaning (semantic similarity)..."} /></div>
        <div style={{ alignSelf: "end" }}><Btn onClick={search} color={mode === "keyword" ? C.blue : C.purple}>Search</Btn></div>
      </div>

      {/* Keyword results */}
      {results?.results?.map((r, i) => <div key={i} style={{ padding: "6px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, marginBottom: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <Pill color={r.doc_type === "requirement" ? C.blue : r.doc_type === "evidence" ? C.teal : C.purple}>{r.doc_type}</Pill>
          <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{r.title}</span>
          <span style={{ fontSize: 9, color: C.emerald, fontWeight: 600 }}>{r.relevance_score}%</span>
        </div>
        <div style={{ fontSize: 10, color: C.muted }}>{r.body?.substring(0, 120)}</div>
      </div>)}
      {results && <div style={{ fontSize: 10, color: C.dim, marginTop: 6 }}>Keyword search: {results.total || 0} results</div>}

      {/* Semantic results */}
      {semResults?.results?.map((r, i) => <div key={i} style={{ padding: "6px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.purple}22`, marginBottom: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <Pill color={r.source_type === "requirement" ? C.blue : C.teal}>{r.source_type}</Pill>
          <span style={{ fontSize: 10, fontWeight: 600, flex: 1 }}>{r.source_id}</span>
          <Pill color={C.purple}>{(r.similarity * 100).toFixed(1)}%</Pill>
          <span style={{ fontSize: 8, color: C.dim }}>{r.model}</span>
        </div>
        <div style={{ fontSize: 10, color: C.muted }}>{r.text?.substring(0, 120)}</div>
      </div>)}
      {semResults && <div style={{ fontSize: 10, color: C.dim, marginTop: 6 }}>Semantic search: {semResults.total || 0} results (cosine similarity)</div>}
    </div>}

    {sub === "embeddings" && <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <Stat label="Embeddings" value={embStats?.total_embeddings || 0} color={C.purple} />
        <Stat label="Dimensions" value={embStats?.dimensions || 0} color={C.blue} />
        <Stat label="Model" value={embStats?.current_mode || "unknown"} color={embStats?.current_mode === "minilm" ? C.emerald : C.amber} />
      </div>
      {embStats?.by_type && <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>By type</div>
        <div style={{ display: "flex", gap: 4 }}>{Object.entries(embStats.by_type).map(([t, c]) => <Pill key={t} color={C.cyan}>{t}: {c}</Pill>)}</div>
      </div>}
      {embStats?.by_model && <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>By model</div>
        <div style={{ display: "flex", gap: 4 }}>{Object.entries(embStats.by_model).map(([m, c]) => <Pill key={m} color={C.purple}>{m}: {c}</Pill>)}</div>
      </div>}
      <div style={{ display: "flex", gap: 6 }}>
        <Btn onClick={buildEmb} color={C.purple}>Build embeddings</Btn>
        <Btn onClick={reembed} color={C.amber} small>Force re-embed all</Btn>
      </div>
      <div style={{ marginTop: 12, padding: 10, borderRadius: 6, background: C.surface, fontSize: 10, color: C.muted, lineHeight: 1.6 }}>
        <strong style={{ color: C.text }}>Model tiers:</strong><br />
        <span style={{ color: C.emerald }}>MiniLM-L6-v2</span> — 384-dim sentence embeddings, baked into Docker image (~90MB). Best accuracy.<br />
        <span style={{ color: C.amber }}>TF-IDF</span> — variable-dim bag-of-words. Zero dependencies. Active when sentence-transformers unavailable.
      </div>
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 10: EVENT BUS (T020 — Kafka/PG NOTIFY)
// ═══════════════════════════════════════════════════════════
function EventBusTab() {
  const [status, setStatus] = useState(null);
  const [wired, setWired] = useState(false);
  const [testResult, setTest] = useState(null);

  const load = async () => { setStatus(await api("/event-bus/status")); };
  useEffect(() => { load(); }, []);

  const wire = async () => { await api("/event-bus/wire", { method: "POST" }); setWired(true); load(); };
  const subNeo4j = async () => { await api("/event-bus/subscribers/neo4j", { method: "POST" }); load(); };
  const subSnap = async () => { await api("/event-bus/subscribers/snapshots", { method: "POST" }); load(); };
  const testPub = async () => { setTest(await api("/event-bus/test-publish", { method: "POST" })); };

  const s = status || {};
  const modeCol = { kafka: C.emerald, pg_notify: C.blue, local: C.amber };

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>Event bus</div>
      <Btn onClick={load} small color={C.teal}>Refresh</Btn>
    </div>

    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
      <Stat label="Mode" value={s.mode || "unknown"} color={modeCol[s.mode] || C.dim} />
      <Stat label="Kafka" value={s.kafka_configured ? "configured" : "off"} color={s.kafka_configured ? C.emerald : C.dim} sub={s.kafka_bootstrap || "not set"} />
      <Stat label="Subscriptions" value={s.total_subscriptions || 0} color={C.cyan} />
      <Stat label="Consumers" value={s.active_consumers?.length || 0} color={C.purple} />
    </div>

    {/* Wiring controls */}
    <div style={{ padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Bus wiring</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <Btn onClick={wire} color={wired ? C.emerald : C.blue} small>{wired ? "Wired ✓" : "Wire emitter → bus"}</Btn>
        <Btn onClick={subNeo4j} color={C.purple} small>+ Neo4j subscriber</Btn>
        <Btn onClick={subSnap} color={C.teal} small>+ Snapshot subscriber</Btn>
        <Btn onClick={testPub} color={C.amber} small>Test publish</Btn>
      </div>
      {testResult && <div style={{ marginTop: 8, fontSize: 10, color: C.muted }}>
        Test: mode={testResult.mode}, event_type={testResult.event_type} {testResult.success ? "✓" : "✗"}
      </div>}
    </div>

    {/* Handler registry */}
    {s.registered_handlers && Object.keys(s.registered_handlers).length > 0 && <div>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Registered handlers</div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {Object.entries(s.registered_handlers).map(([k, v]) =>
          <Pill key={k} color={k === "*" ? C.purple : C.cyan}>{k}: {v}</Pill>
        )}
      </div>
    </div>}

    {/* Mode explanation */}
    <div style={{ marginTop: 14, padding: 10, borderRadius: 6, background: C.surface, fontSize: 10, color: C.muted, lineHeight: 1.6 }}>
      <strong style={{ color: C.text }}>Mode explanation:</strong><br />
      <span style={{ color: C.emerald }}>kafka</span> — full event streaming with consumer groups (set KAFKA_BOOTSTRAP_SERVERS)<br />
      <span style={{ color: C.blue }}>pg_notify</span> — PostgreSQL LISTEN/NOTIFY for single-instance (live PG required)<br />
      <span style={{ color: C.amber }}>local</span> — in-memory dispatch (dev/test, no external dependencies)
    </div>
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 11: LINEAGE GRAPH (T034-T036)
// ═══════════════════════════════════════════════════════════
function LineageTab() {
  const [mode, setMode] = useState(null);
  const [stats, setStats] = useState(null);
  const [trace, setTrace] = useState(null);
  const [provenance, setProvenance] = useState(null);
  const [agentHist, setAgentHist] = useState(null);
  const [traceId, setTraceId] = useState("");
  const [claimId, setClaimId] = useState("");
  const [agentId, setAgentId] = useState("maria");
  const [sub, setSub] = useState("overview");

  const load = async () => {
    setMode(await api("/lineage/mode"));
    setStats(await api("/lineage/stats"));
  };
  useEffect(() => { load(); }, []);

  const sync = async () => { await api("/lineage/sync", { method: "POST" }); load(); };
  const initSchema = async () => { await api("/lineage/schema", { method: "POST" }); load(); };
  const traceEvidence = async () => { if (traceId) setTrace(await api(`/lineage/evidence/${traceId}/trace`)); };
  const traceClaim = async () => { if (claimId) setProvenance(await api(`/lineage/claim/${claimId}/provenance`)); };
  const queryAgent = async () => { if (agentId) setAgentHist(await api(`/lineage/agent/${agentId}`)); };

  const st = stats || {};
  const liveColor = mode?.live ? C.emerald : C.amber;

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>PROV lineage graph</div>
      <div style={{ display: "flex", gap: 4 }}>
        <Btn onClick={initSchema} small color={C.purple}>Init schema</Btn>
        <Btn onClick={sync} small color={C.teal}>Sync events</Btn>
      </div>
    </div>

    <SubTab tabs={[{ id: "overview", l: "Overview" }, { id: "evidence", l: "Evidence trace" }, { id: "claim", l: "Claim provenance" }, { id: "agent", l: "Agent history" }]} active={sub} onSet={setSub} />

    {sub === "overview" && <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <Stat label="Mode" value={mode?.mode || "unknown"} color={liveColor} />
        <Stat label="Agents" value={st.agents || st.agent || 0} color={C.purple} />
        <Stat label="Activities" value={st.activities || st.activity || 0} color={C.cyan} />
        <Stat label="Entities" value={st.entities || st.proventity || 0} color={C.blue} />
        <Stat label="Edges" value={st.edges || st.relationships || 0} color={C.amber} />
      </div>

      {/* Visual PROV diagram */}
      <div style={{ background: C.card, borderRadius: 10, border: `1px solid ${C.border}`, padding: 16, marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 10 }}>W3C PROV-DM model</div>
        <svg width="100%" height="140" viewBox="0 0 500 140">
          {/* Agent */}
          <polygon points="80,20 160,20 170,50 150,80 90,80 70,50" fill={C.purple + "22"} stroke={C.purple} strokeWidth="1.5" />
          <text x="120" y="48" textAnchor="middle" fill={C.purple} fontSize="10" fontWeight="600">Agent</text>
          <text x="120" y="62" textAnchor="middle" fill={C.muted} fontSize="8">{st.agents || 0} nodes</text>

          {/* Activity */}
          <rect x="215" y="20" width="120" height="60" rx="4" fill={C.cyan + "22"} stroke={C.cyan} strokeWidth="1.5" />
          <text x="275" y="48" textAnchor="middle" fill={C.cyan} fontSize="10" fontWeight="600">Activity</text>
          <text x="275" y="62" textAnchor="middle" fill={C.muted} fontSize="8">{st.activities || 0} nodes</text>

          {/* Entity */}
          <ellipse cx="430" cy="50" rx="55" ry="30" fill={C.blue + "22"} stroke={C.blue} strokeWidth="1.5" />
          <text x="430" y="48" textAnchor="middle" fill={C.blue} fontSize="10" fontWeight="600">ProvEntity</text>
          <text x="430" y="62" textAnchor="middle" fill={C.muted} fontSize="8">{st.entities || 0} nodes</text>

          {/* Edges */}
          <line x1="160" y1="45" x2="215" y2="45" stroke={C.muted} strokeWidth="1" markerEnd="url(#arrow)" />
          <text x="187" y="38" textAnchor="middle" fill={C.dim} fontSize="7">wasAssociatedWith</text>
          <line x1="335" y1="45" x2="375" y2="45" stroke={C.muted} strokeWidth="1" markerEnd="url(#arrow)" />
          <text x="355" y="38" textAnchor="middle" fill={C.dim} fontSize="7">used / generated</text>

          {/* Derivation */}
          <path d="M430 80 C430 110, 430 110, 430 130" stroke={C.amber} strokeWidth="1" fill="none" strokeDasharray="3 2" />
          <text x="430" y="125" textAnchor="middle" fill={C.amber} fontSize="7">wasDerivedFrom</text>

          <defs><marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6" fill={C.muted} /></marker></defs>
        </svg>
      </div>
    </div>}

    {sub === "evidence" && <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}><Inp label="Evidence ID" value={traceId} onChange={setTraceId} placeholder="ev_..." /></div>
        <div style={{ alignSelf: "end" }}><Btn onClick={traceEvidence}>Trace</Btn></div>
      </div>
      {trace && <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, padding: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{trace.evidence_name || trace.evidence_id}</div>
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 8 }}>Hash: {trace.content_hash?.substring(0, 24)} | Mode: {trace.graph_mode}</div>
        {trace.linked_requirements?.length > 0 && <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Linked requirements ({trace.linked_requirements.length})</div>
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>{trace.linked_requirements.slice(0, 10).map(r => <Pill key={r} color={C.blue}>{r}</Pill>)}</div>
        </div>}
        {trace.locators?.length > 0 && <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Locators ({trace.locators.length})</div>
          {trace.locators.map((l, i) => <div key={i} style={{ fontSize: 9, color: C.muted, padding: "2px 0" }}>p.{l.page} — {l.text_excerpt}</div>)}
        </div>}
        {trace.activity_chain?.length > 0 && <div>
          <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Activity chain ({trace.activity_chain.length})</div>
          {trace.activity_chain.map((a, i) => <div key={i} style={{ display: "flex", gap: 4, fontSize: 9, padding: "2px 0" }}>
            <Pill color={C.cyan}>{a.event_type || a.event}</Pill>
            <span style={{ color: C.dim }}>{a.agent_id || a.actor}</span>
          </div>)}
        </div>}
      </div>}
    </div>}

    {sub === "claim" && <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}><Inp label="Claim ID" value={claimId} onChange={setClaimId} placeholder="cl_..." /></div>
        <div style={{ alignSelf: "end" }}><Btn onClick={traceClaim}>Provenance</Btn></div>
      </div>
      {provenance && <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, padding: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{provenance.claim_id}</div>
        <div style={{ fontSize: 10, color: C.text, marginBottom: 6 }}>{provenance.claim_text?.substring(0, 150)}</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <Pill color={provenance.ai_generated ? C.purple : C.blue}>{provenance.ai_generated ? "AI-generated" : "Human"}</Pill>
          {provenance.promoted_from && <Pill color={C.amber}>Promoted from {provenance.promoted_from}</Pill>}
        </div>
        {provenance.evidence_ids?.length > 0 && <div>
          <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }}>Evidence chain</div>
          {provenance.evidence_ids.map(e => <Pill key={e} color={C.teal}>{e}</Pill>)}
        </div>}
      </div>}
    </div>}

    {sub === "agent" && <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}><Inp label="Agent ID" value={agentId} onChange={setAgentId} placeholder="maria, ai_service, system" /></div>
        <div style={{ alignSelf: "end" }}><Btn onClick={queryAgent}>Query</Btn></div>
      </div>
      {agentHist && <div>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>{agentHist.agent_id}: {agentHist.total} activities</div>
        {agentHist.activities?.slice(0, 20).map((a, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 4, background: C.card, marginBottom: 2, fontSize: 10 }}>
          <Pill color={C.cyan}>{a.event_type || a.event}</Pill>
          <span style={{ color: C.muted, flex: 1 }}>{a.emitted_at || ""}</span>
          {a.affected_entities && <span style={{ fontSize: 8, color: C.dim }}>{a.affected_entities.length} entities</span>}
        </div>)}
      </div>}
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 12: SNAPSHOTS (G25)
// ═══════════════════════════════════════════════════════════
function SnapshotsTab() {
  const [snapshots, setSnaps] = useState(null);
  const [recon, setRecon] = useState(null);
  const [intTest, setIntTest] = useState(null);
  const [ts, setTs] = useState("2030-01-01T00:00:00+00:00");
  const [sub, setSub] = useState("manage");

  const load = async () => { setSnaps(await api("/snapshots")); };
  useEffect(() => { load(); }, []);

  const create = async () => { await api("/snapshots/create", { method: "POST" }); load(); };
  const autoSnap = async () => { const r = await api("/snapshots/auto", { method: "POST" }); load(); return r; };
  const reconstruct = async () => { setRecon(await api(`/snapshots/reconstruct?timestamp=${encodeURIComponent(ts)}`)); };
  const runIntegration = async () => { setIntTest(await api("/integration-test/event-worm")); };

  const snaps = snapshots?.snapshots || [];

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>Snapshots + reconstruction</div>
      <Btn onClick={load} small color={C.teal}>Refresh</Btn>
    </div>

    <SubTab tabs={[{ id: "manage", l: "Manage" }, { id: "reconstruct", l: "Reconstruct" }, { id: "integration", l: "Integration test" }]} active={sub} onSet={setSub} />

    {sub === "manage" && <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <Btn onClick={create} color={C.emerald}>Create snapshot</Btn>
        <Btn onClick={autoSnap} color={C.blue} small>Auto (every 100 events)</Btn>
      </div>
      <Stat label="Total snapshots" value={snaps.length} color={C.cyan} />
      <div style={{ marginTop: 10 }}>
        {snaps.map((s, i) => <div key={i} style={{ padding: "6px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, marginBottom: 3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
            <Pill color={C.emerald}>{s.snapshot_type}</Pill>
            <span style={{ fontSize: 10, fontWeight: 600 }}>Event #{s.event_sequence_at}</span>
            <span style={{ fontSize: 9, color: C.dim, fontFamily: "monospace" }}>{s.chain_hash_at?.substring(0, 16)}</span>
          </div>
          <div style={{ fontSize: 9, color: C.muted }}>ID: {s.event_id_at} | Created: {s.created_at} | Keys: {s.state_keys?.join(", ")}</div>
        </div>)}
      </div>
    </div>}

    {sub === "reconstruct" && <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}><Inp label="Reconstruct at timestamp" value={ts} onChange={setTs} placeholder="2030-01-01T00:00:00+00:00" /></div>
        <div style={{ alignSelf: "end" }}><Btn onClick={reconstruct}>Reconstruct</Btn></div>
      </div>
      {recon && <div style={{ background: C.card, borderRadius: 8, border: `1px solid ${recon.used_snapshot ? C.emerald : C.amber}33`, padding: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          <Stat label="Requirements" value={recon.total_requirements || 0} />
          <Stat label="Satisfied" value={recon.satisfied || 0} color={C.emerald} />
          <Stat label="Compliance" value={`${recon.compliance_pct || 0}%`} color={C.cyan} />
          <Stat label="Events replayed" value={recon.events_replayed || 0} color={C.blue} />
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <Pill color={recon.used_snapshot ? C.emerald : C.amber}>{recon.used_snapshot ? "Snapshot-accelerated" : "Full replay"}</Pill>
          {recon.snapshot_id && <Pill color={C.purple}>Snapshot #{recon.snapshot_id}</Pill>}
        </div>
        {recon.by_status && <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {Object.entries(recon.by_status).map(([s, c]) => <Pill key={s} color={s === "open" ? C.muted : s === "evidenced" ? C.blue : s === "claimed" ? C.amber : C.emerald}>{s}: {c}</Pill>)}
        </div>}
      </div>}
    </div>}

    {sub === "integration" && <div>
      <Btn onClick={runIntegration} color={C.purple}>Run T019 integration test</Btn>
      {intTest && <div style={{ marginTop: 12 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <Stat label="Total" value={intTest.total || 0} />
          <Stat label="Passed" value={intTest.passed || 0} color={C.emerald} />
          <Stat label="Failed" value={intTest.failed || 0} color={intTest.failed > 0 ? C.red : C.emerald} />
          <Stat label="Rate" value={`${intTest.pass_rate || 0}%`} color={intTest.pass_rate === 100 ? C.emerald : C.amber} />
        </div>
        {intTest.results?.map((r, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 4, background: C.card, marginBottom: 2, fontSize: 10 }}>
          <span style={{ color: r.pass ? C.emerald : C.red, fontWeight: 700, width: 14 }}>{r.pass ? "✓" : "✗"}</span>
          <span style={{ fontWeight: 600, flex: 1 }}>{r.test}</span>
          <span style={{ color: C.muted, fontSize: 9, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.detail}</span>
        </div>)}
      </div>}
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 13: AI AGENTS (6 specialized agents)
// ═══════════════════════════════════════════════════════════
function AIAgentsTab() {
  const [agents, setAgents] = useState([]);
  const [sub, setSub] = useState("overview");
  const [dqValue, setDqValue] = useState("127450");
  const [dqUnit, setDqUnit] = useState("tCO2e");
  const [dqLabel, setDqLabel] = useState("Scope 1 GHG emissions");
  const [dqResult, setDqResult] = useState(null);
  const [efActivity, setEfActivity] = useState("natural gas combustion");
  const [efRegion, setEfRegion] = useState("EU");
  const [efResult, setEfResult] = useState(null);
  const [s3Activity, setS3Activity] = useState("Business travel by air");
  const [s3Spend, setS3Spend] = useState("250000");
  const [s3Result, setS3Result] = useState(null);
  const [auditResult, setAuditResult] = useState(null);
  const [gapResult, setGapResult] = useState(null);
  const [mapResult, setMapResult] = useState(null);
  const [piiText, setPiiText] = useState("Contact John at john@example.com or call +1-555-123-4567. SSN: 123-45-6789");
  const [piiMethod, setPiiMethod] = useState("replace");
  const [piiResult, setPiiResult] = useState(null);
  const [pdfStatus, setPdfStatus] = useState(null);
  const [piiStatus, setPiiStatus] = useState(null);
  const [loading, setLoading] = useState("");
  const [pipeStatus, setPipeStatus] = useState(null);

  const load = async () => {
    const r = await api("/ai/agents");
    setAgents(r?.agents || []);
    setPipeStatus(await api("/ai/pipeline-status"));
    setPdfStatus(await api("/ai/pdf/status"));
    setPiiStatus(await api("/ai/pii/status"));
  };
  useEffect(() => { load(); }, []);

  const runDq = async () => { setLoading("dq"); setDqResult(await api("/ai/quality/check", { method: "POST", body: { datapoint_value: dqValue, datapoint_unit: dqUnit, datapoint_label: dqLabel } })); setLoading(""); };
  const runEf = async () => { setLoading("ef"); setEfResult(await api("/ai/ef/match", { method: "POST", body: { activity_desc: efActivity, region: efRegion } })); setLoading(""); };
  const runS3 = async () => { setLoading("s3"); setS3Result(await api("/ai/scope3/estimate", { method: "POST", body: { activities: [{ description: s3Activity, spend_eur: Number(s3Spend) }] } })); setLoading(""); };
  const runAudit = async () => { setLoading("audit"); setAuditResult(await api("/ai/ghg/audit", { method: "POST", body: { scope1: 127450, scope2_market: 42100, scope2_location: 48200 } })); setLoading(""); };
  const runGap = async () => { setLoading("gap"); setGapResult(await api("/ai/regulatory/gaps", { method: "POST", body: { entity_id: "ent_001" } })); setLoading(""); };
  const runMap = async () => { setLoading("map"); setMapResult(await api("/ai/multi-framework/map", { method: "POST", body: { requirement_text: "Gross Scope 1 GHG emissions in tCO2e" } })); setLoading(""); };
  const runPii = async () => { setLoading("pii"); setPiiResult(await api("/ai/pii/redact", { method: "POST", body: { text: piiText, method: piiMethod } })); setLoading(""); };

  const AGENT_COL = { "data_quality": C.teal, "emission_factor": C.amber, "scope3_estimator": C.orange, "ghg_auditor": C.emerald, "regulatory_gap": C.red, "multi_fw_mapper": C.purple };

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>AI agents <Pill color={C.purple}>{agents.length} agents</Pill></div>
      <Btn onClick={load} small color={C.teal}>Refresh</Btn>
    </div>
    <SubTab tabs={[{ id: "overview", l: "Overview" }, { id: "dq", l: "Data Quality" }, { id: "ef", l: "Emission Factor" }, { id: "s3", l: "Scope 3" }, { id: "audit", l: "GHG Audit" }, { id: "gap", l: "Reg. Gaps" }, { id: "map", l: "Multi-FW" }, { id: "pii", l: "PII Scanner" }]} active={sub} onSet={setSub} />

    {sub === "overview" && <div>
      {pipeStatus && <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <Stat label="Pipeline" value={pipeStatus.pipeline_ready ? "READY" : "DEGRADED"} color={pipeStatus.pipeline_ready ? C.emerald : C.amber} />
        <Stat label="Agents" value={pipeStatus.active_agents || agents.length} color={C.purple} />
        <Stat label="Neo4j" value={pipeStatus.neo4j_connected ? "Connected" : "Disconnected"} color={pipeStatus.neo4j_connected ? C.emerald : C.red} />
        <Stat label="Embedding" value={pipeStatus.embedding_model || "unknown"} color={C.cyan} />
        {pdfStatus && <Stat label="PDF Parser" value={pdfStatus.available ? pdfStatus.parser : "unavailable"} color={pdfStatus.available ? C.emerald : C.dim} />}
        {piiStatus && <Stat label="PII Detection" value={piiStatus.mode} color={C.purple} />}
      </div>}
      {agents.map(a => <div key={a.agent_id} data-testid={`agent-card-${a.agent_id}`} style={{ padding: "10px 12px", borderRadius: 8, background: C.card, border: `1px solid ${(AGENT_COL[a.agent_id] || C.blue)}22`, marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Pill color={AGENT_COL[a.agent_id] || C.blue}>{a.id}</Pill>
          <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{a.name}</span>
          <Pill color={a.circuit?.state === "closed" ? C.emerald : C.red}>{a.circuit?.state || "unknown"}</Pill>
        </div>
        <div style={{ display: "flex", gap: 12, fontSize: 10, color: C.muted }}>
          <span>Timeout: {a.timeout}s</span><span>Retries: {a.retries}</span><span>Failures: {a.circuit?.failures || 0}</span>
        </div>
      </div>)}
    </div>}

    {sub === "dq" && <div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>AI-01: Data Quality Agent</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
        <Inp label="Value" value={dqValue} onChange={setDqValue} placeholder="127450" />
        <Inp label="Unit" value={dqUnit} onChange={setDqUnit} placeholder="tCO2e" />
        <Inp label="Label" value={dqLabel} onChange={setDqLabel} placeholder="Scope 1 GHG emissions" />
      </div>
      <Btn onClick={runDq} disabled={loading === "dq"} color={C.teal}>{loading === "dq" ? "Checking..." : "Check quality"}</Btn>
      {dqResult && !dqResult.error && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.teal}33` }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <Stat label="Score" value={dqResult.quality_score?.toFixed(1)} color={dqResult.quality_score >= 4 ? C.emerald : dqResult.quality_score >= 3 ? C.amber : C.red} />
          <Stat label="Label" value={dqResult.quality_label} color={C.teal} />
          <Stat label="Flags" value={dqResult.flag_count || 0} color={dqResult.flag_count > 0 ? C.amber : C.emerald} />
        </div>
        {dqResult.flags?.map((f, i) => <div key={i} style={{ fontSize: 10, padding: "4px 8px", borderRadius: 4, background: C.surface, marginBottom: 2 }}>
          <Pill color={f.severity === "warning" ? C.amber : f.severity === "error" ? C.red : C.dim}>{f.severity}</Pill>
          <span style={{ marginLeft: 6, color: C.muted }}>{f.message}</span>
        </div>)}
      </div>}
    </div>}

    {sub === "ef" && <div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>AI-02: Emission Factor Agent</div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, marginBottom: 10 }}>
        <Inp label="Activity" value={efActivity} onChange={setEfActivity} placeholder="natural gas combustion" />
        <Inp label="Region" value={efRegion} onChange={setEfRegion} placeholder="EU" />
      </div>
      <Btn onClick={runEf} disabled={loading === "ef"} color={C.amber}>{loading === "ef" ? "Matching..." : "Match emission factors"}</Btn>
      {efResult?.results?.length > 0 && <div style={{ marginTop: 12 }}>
        {efResult.results.slice(0, 5).map((r, i) => <div key={i} style={{ padding: "8px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.amber}22`, marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600 }}>{r.activity}</span>
            <span style={{ flex: 1 }} />
            <Pill color={C.amber}>{r.value} {r.unit_out}/{r.unit_in}</Pill>
          </div>
          <div style={{ display: "flex", gap: 8, fontSize: 10, color: C.muted }}>
            <span>{r.source}</span><span>{r.geography}</span><span>Scope {r.scope}</span>
            <span>Conf: {r.confidence}%</span>
          </div>
        </div>)}
      </div>}
    </div>}

    {sub === "s3" && <div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>AI-03: Scope 3 Estimator</div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, marginBottom: 10 }}>
        <Inp label="Activity description" value={s3Activity} onChange={setS3Activity} placeholder="Business travel by air" />
        <Inp label="Spend (EUR)" value={s3Spend} onChange={setS3Spend} type="number" placeholder="250000" />
      </div>
      <Btn onClick={runS3} disabled={loading === "s3"} color={C.orange}>{loading === "s3" ? "Estimating..." : "Estimate Scope 3"}</Btn>
      {s3Result && !s3Result.error && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.orange}33` }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <Stat label="Total tCO2e" value={s3Result.total_tco2e?.toFixed(1) || s3Result.estimates?.[0]?.tco2e?.toFixed(1) || "—"} color={C.orange} />
          <Stat label="Method" value={s3Result.estimates?.[0]?.method || "spend-based"} color={C.cyan} />
        </div>
        {s3Result.estimates?.map((e, i) => <div key={i} style={{ fontSize: 10, color: C.muted, padding: "4px 8px", background: C.surface, borderRadius: 4, marginBottom: 2 }}>
          Cat {e.ghg_category}: {e.description} — <span style={{ color: C.orange, fontWeight: 600 }}>{e.tco2e?.toFixed(1)} tCO2e</span> ({e.method}, conf: {(e.confidence * 100).toFixed(0)}%)
        </div>)}
      </div>}
    </div>}

    {sub === "audit" && <div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>AI-05: GHG Auditor</div>
      <Btn onClick={runAudit} disabled={loading === "audit"} color={C.emerald}>{loading === "audit" ? "Auditing..." : "Run GHG audit"}</Btn>
      {auditResult && !auditResult.error && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.emerald}33` }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <Stat label="Overall" value={auditResult.overall_assessment || "—"} color={C.emerald} />
          <Stat label="Issues" value={auditResult.issues?.length || 0} color={(auditResult.issues?.length || 0) > 0 ? C.amber : C.emerald} />
        </div>
        {auditResult.checks?.map((c, i) => <div key={i} style={{ fontSize: 10, padding: "4px 8px", borderRadius: 4, background: C.surface, marginBottom: 2 }}>
          <Pill color={c.pass ? C.emerald : C.red}>{c.pass ? "PASS" : "FAIL"}</Pill>
          <span style={{ marginLeft: 6, color: C.text, fontWeight: 600 }}>{c.check}</span>
          <span style={{ marginLeft: 6, color: C.muted }}>{c.detail}</span>
        </div>)}
      </div>}
    </div>}

    {sub === "gap" && <div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>AI-14a: Regulatory Gap Finder</div>
      <Btn onClick={runGap} disabled={loading === "gap"} color={C.red}>{loading === "gap" ? "Scanning..." : "Scan regulatory gaps"}</Btn>
      {gapResult && !gapResult.error && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.red}33` }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <Stat label="Gaps found" value={gapResult.total_gaps || gapResult.gaps?.length || 0} color={C.red} />
          <Stat label="Critical" value={gapResult.gaps?.filter(g => g.severity === "critical").length || 0} color={C.red} />
        </div>
        {gapResult.gaps?.slice(0, 10).map((g, i) => <div key={i} style={{ fontSize: 10, padding: "4px 8px", borderRadius: 4, background: C.surface, marginBottom: 2 }}>
          <Pill color={PRI_COL[g.severity] || C.amber}>{g.severity || g.priority}</Pill>
          <span style={{ marginLeft: 6, color: C.text }}>{g.gap || g.description}</span>
        </div>)}
      </div>}
    </div>}

    {sub === "map" && <div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>AI-14b: Multi-Framework Mapper</div>
      <Btn onClick={runMap} disabled={loading === "map"} color={C.purple}>{loading === "map" ? "Mapping..." : "Map across frameworks"}</Btn>
      {mapResult && !mapResult.error && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.purple}33` }}>
        {mapResult.mappings?.slice(0, 10).map((m, i) => <div key={i} style={{ fontSize: 10, padding: "4px 8px", borderRadius: 4, background: C.surface, marginBottom: 2 }}>
          <Pill color={C.blue}>{m.framework}</Pill>
          <span style={{ marginLeft: 6, color: C.text }}>{m.requirement_id || m.reference}</span>
          <Pill color={C.purple}>{((m.similarity || m.score || 0) * 100).toFixed(0)}%</Pill>
        </div>)}
      </div>}
    </div>}

    {sub === "pii" && <div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>PII Scanner + Redaction</div>
      <textarea data-testid="pii-input" value={piiText} onChange={e => setPiiText(e.target.value)} rows={4} style={{ width: "100%", background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, fontSize: 11, fontFamily: "monospace", resize: "vertical", marginBottom: 8 }} placeholder="Paste text to scan for PII..." />
      <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
        <select data-testid="pii-method" value={piiMethod} onChange={e => setPiiMethod(e.target.value)} style={{ background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 4, padding: "4px 8px", fontSize: 11 }}>
          <option value="replace">Replace (tags)</option>
          <option value="hash">Hash (pseudonymize)</option>
          <option value="mask">Mask (****)</option>
        </select>
        <Btn onClick={runPii} disabled={loading === "pii"} color={C.purple}>{loading === "pii" ? "Scanning..." : "Scan + redact"}</Btn>
      </div>
      {piiResult && !piiResult.error && <div style={{ padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.purple}33` }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <Stat label="Redactions" value={piiResult.redactions} color={piiResult.redactions > 0 ? C.red : C.emerald} />
          <Stat label="Method" value={piiResult.method} color={C.purple} />
        </div>
        <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 4, color: C.muted }}>Redacted output:</div>
        <div data-testid="pii-output" style={{ fontSize: 11, padding: 10, borderRadius: 4, background: C.surface, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", lineHeight: 1.6 }}>{piiResult.redacted_text}</div>
        {piiResult.entities_found?.length > 0 && <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 4, color: C.muted }}>Entities detected:</div>
          {piiResult.entities_found.map((e, i) => <div key={i} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: C.surface, marginBottom: 1, display: "inline-flex", alignItems: "center", gap: 4, marginRight: 4 }}>
            <Pill color={e.severity === "critical" ? C.red : e.severity === "high" ? C.orange : C.amber}>{e.type}</Pill>
            <span style={{ color: C.muted }}>{e.text}</span>
          </div>)}
        </div>}
      </div>}
      {piiStatus && <div style={{ marginTop: 12, fontSize: 10, color: C.dim }}>
        Mode: {piiStatus.mode} | Patterns: {piiStatus.regex_patterns} | Types: {piiStatus.pii_types_detected?.join(", ")}
        {piiStatus.install_hint && <span style={{ color: C.amber }}> | Upgrade: {piiStatus.install_hint}</span>}
      </div>}
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 14: GRAPHRAG (Knowledge graph + RAG)
// ═══════════════════════════════════════════════════════════
function GraphRAGTab() {
  const [stats, setStats] = useState(null);
  const [sub, setSub] = useState("stats");
  const [schemaResult, setSchemaResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [cfA, setCfA] = useState("CSRD");
  const [cfB, setCfB] = useState("GRI");
  const [crossFw, setCrossFw] = useState(null);
  const [searchQ, setSearchQ] = useState("Scope 1 GHG emissions");
  const [searchRes, setSearchRes] = useState(null);
  const [loading, setLoading] = useState("");

  const load = async () => { setStats(await api("/graphrag/stats")); };
  useEffect(() => { load(); }, []);

  const syncSchema = async () => { setLoading("schema"); setSchemaResult(await api("/graphrag/schema", { method: "POST" })); setLoading(""); load(); };
  const syncDocs = async () => { setLoading("docs"); setSyncResult(await api("/graphrag/sync", { method: "POST" })); setLoading(""); load(); };
  const queryCross = async () => { setCrossFw(await api(`/graphrag/cross-framework?framework_a=${cfA}&framework_b=${cfB}`)); };
  const searchGraph = async () => { setLoading("search"); setSearchRes(await api("/graphrag/search", { method: "POST", body: { query: searchQ, top_k: 10 } })); setLoading(""); };

  const s = stats || {};

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>GraphRAG <Pill color={C.cyan}>Neo4j</Pill></div>
      <Btn onClick={load} small color={C.teal}>Refresh</Btn>
    </div>
    <SubTab tabs={[{ id: "stats", l: "Stats" }, { id: "schema", l: "Schema" }, { id: "cross", l: "Cross-framework" }, { id: "search", l: "Graph search" }]} active={sub} onSet={setSub} />

    {sub === "stats" && <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <Stat label="Frameworks" value={s.framework || 0} color={C.blue} />
        <Stat label="Topics" value={s.disclosuretopic || 0} color={C.teal} />
        <Stat label="Requirements" value={s.requirementnode || 0} color={C.purple} />
        <Stat label="Chunks" value={s.chunk || 0} color={C.cyan} />
        <Stat label="DataPoints" value={s.datapoint || 0} color={C.amber} />
        <Stat label="SATISFIES" value={s.satisfies_edges || 0} color={C.emerald} />
        <Stat label="OVERLAPS" value={s.overlaps || 0} color={C.orange} />
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Stat label="Documents" value={s.document || 0} />
        <Stat label="Pages" value={s.page || 0} />
        <Stat label="Sections" value={s.section || 0} />
        <Stat label="Mode" value={s.mode || "unknown"} color={s.mode === "neo4j" ? C.emerald : C.amber} />
      </div>
    </div>}

    {sub === "schema" && <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Btn onClick={syncSchema} disabled={loading === "schema"} color={C.blue}>{loading === "schema" ? "Syncing..." : "Sync schema constraints"}</Btn>
        <Btn onClick={syncDocs} disabled={loading === "docs"} color={C.purple}>{loading === "docs" ? "Syncing..." : "Sync all documents"}</Btn>
      </div>
      {schemaResult && <div style={{ padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${schemaResult.success ? C.emerald : C.red}33`, marginBottom: 8 }}>
        <Pill color={schemaResult.success ? C.emerald : C.red}>{schemaResult.success ? "Success" : "Failed"}</Pill>
        <span style={{ marginLeft: 8, fontSize: 11 }}>{schemaResult.constraints} constraints, {schemaResult.total} total</span>
        {schemaResult.errors && <div style={{ fontSize: 10, color: C.red, marginTop: 4 }}>{schemaResult.errors}</div>}
      </div>}
      {syncResult && <div style={{ padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.purple}33` }}>
        <div style={{ fontSize: 11 }}>Synced: {syncResult.synced ?? syncResult.documents_synced ?? 0} documents</div>
      </div>}
    </div>}

    {sub === "cross" && <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "end" }}>
        <Inp label="Framework A" value={cfA} onChange={setCfA} />
        <Inp label="Framework B" value={cfB} onChange={setCfB} />
        <Btn onClick={queryCross} color={C.orange}>Compare</Btn>
      </div>
      {crossFw && <div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <Stat label="Overlaps" value={crossFw.total || 0} color={C.orange} />
          <Stat label="Mode" value={crossFw.mode || "unknown"} color={crossFw.mode === "neo4j" ? C.emerald : C.amber} />
        </div>
        {crossFw.overlaps?.slice(0, 15).map((o, i) => <div key={i} style={{ padding: "6px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.orange}22`, marginBottom: 3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Pill color={C.blue}>{o.ref_a || o.req_a}</Pill>
            <span style={{ color: C.dim }}>—</span>
            <Pill color={C.purple}>{o.ref_b || o.req_b}</Pill>
            <span style={{ flex: 1 }} />
            <Pill color={C.orange}>{((o.similarity || 0) * 100).toFixed(0)}%</Pill>
          </div>
        </div>)}
      </div>}
    </div>}

    {sub === "search" && <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "end" }}>
        <div style={{ flex: 1 }}><Inp label="Graph search query" value={searchQ} onChange={setSearchQ} placeholder="Scope 1 GHG emissions" /></div>
        <Btn onClick={searchGraph} disabled={loading === "search"} color={C.cyan}>{loading === "search" ? "Searching..." : "Search"}</Btn>
      </div>
      {searchRes?.results?.map((r, i) => <div key={i} style={{ padding: "6px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.cyan}22`, marginBottom: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <Pill color={C.cyan}>{r.label || r.type || "node"}</Pill>
          <span style={{ fontSize: 10, fontWeight: 600, flex: 1 }}>{r.name || r.text || r.id}</span>
          {r.score && <Pill color={C.purple}>{(r.score * 100).toFixed(0)}%</Pill>}
        </div>
        {r.properties && <div style={{ fontSize: 9, color: C.muted }}>{JSON.stringify(r.properties).substring(0, 120)}</div>}
      </div>)}
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// TAB 15: EVALUATION (RAGAS + Golden Dataset + Feedback)
// ═══════════════════════════════════════════════════════════
function EvaluationTab() {
  const [sub, setSub] = useState("golden");
  const [golden, setGolden] = useState(null);
  const [evalResult, setEvalResult] = useState(null);
  const [evalHistory, setEvalHistory] = useState(null);
  const [fbStats, setFbStats] = useState(null);
  const [trainingData, setTrainingData] = useState(null);
  const [loading, setLoading] = useState("");

  const load = async () => {
    setGolden(await api("/eval/golden-dataset"));
    setEvalHistory(await api("/eval/history"));
    setFbStats(await api("/ai/feedback/stats"));
  };
  useEffect(() => { load(); }, []);

  const runEval = async () => { setLoading("eval"); setEvalResult(await api("/eval/run", { method: "POST" })); setLoading(""); load(); };
  const exportTraining = async () => { setLoading("export"); setTrainingData(await api("/ai/training-export")); setLoading(""); };
  const exportGolden = async () => { setLoading("golden-export"); setTrainingData(await api("/ai/training-export/golden")); setLoading(""); };

  return <div>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>Evaluation + training <Pill color={C.emerald}>RAGAS</Pill></div>
      <Btn onClick={load} small color={C.teal}>Refresh</Btn>
    </div>
    <SubTab tabs={[{ id: "golden", l: `Golden (${golden?.total || 0})` }, { id: "run", l: "Run eval" }, { id: "history", l: "History" }, { id: "feedback", l: "Feedback" }, { id: "export", l: "Training export" }]} active={sub} onSet={setSub} />

    {sub === "golden" && <div>
      <Stat label="Golden dataset" value={golden?.total || 0} color={C.emerald} sub="Curated ground-truth claim/evidence pairs" />
      <div style={{ marginTop: 12 }}>
        {golden?.dataset?.slice(0, 20).map((g, i) => <div key={i} data-testid={`golden-item-${g.id}`} style={{ padding: "8px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <Pill color={C.emerald}>{g.expected_status}</Pill>
            <span style={{ fontSize: 10, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.requirement_text?.substring(0, 80)}</span>
            <span style={{ fontSize: 9, color: C.dim }}>{g.id}</span>
          </div>
          <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>{g.evidence_text?.substring(0, 120)}</div>
          <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
            {g.expected_keywords?.slice(0, 5).map((k, j) => <Pill key={j} color={C.cyan}>{k}</Pill>)}
            <Pill color={C.blue}>{g.topic}</Pill>
          </div>
        </div>)}
      </div>
    </div>}

    {sub === "run" && <div>
      <Btn onClick={runEval} disabled={loading === "eval"} color={C.emerald}>{loading === "eval" ? "Running RAGAS evaluation..." : "Run RAGAS evaluation"}</Btn>
      {evalResult && !evalResult.error && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.emerald}33` }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          <Stat label="Faithfulness" value={evalResult.faithfulness?.toFixed(3) || "—"} color={C.emerald} />
          <Stat label="Relevancy" value={evalResult.answer_relevancy?.toFixed(3) || "—"} color={C.blue} />
          <Stat label="Recall" value={evalResult.context_recall?.toFixed(3) || "—"} color={C.purple} />
          <Stat label="Precision" value={evalResult.context_precision?.toFixed(3) || "—"} color={C.cyan} />
          <Stat label="Keyword hit" value={`${((evalResult.keyword_hit_rate || 0) * 100).toFixed(1)}%`} color={C.amber} />
          <Stat label="Samples" value={evalResult.sample_size || 0} color={C.teal} />
        </div>
        {evalResult.overall_score && <Bar pct={evalResult.overall_score * 100} color={C.emerald} h={10} />}
        <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>Overall: {((evalResult.overall_score || 0) * 100).toFixed(1)}%</div>
      </div>}
    </div>}

    {sub === "history" && <div>
      {evalHistory?.runs?.map((r, i) => <div key={i} style={{ padding: "8px 10px", borderRadius: 6, background: C.card, border: `1px solid ${C.border}`, marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 600 }}>Run #{i + 1}</span>
          <Pill color={C.emerald}>F: {r.faithfulness?.toFixed(2)}</Pill>
          <Pill color={C.blue}>R: {r.answer_relevancy?.toFixed(2)}</Pill>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 9, color: C.dim }}>{r.timestamp || r.created_at}</span>
        </div>
      </div>)}
      {(!evalHistory?.runs || evalHistory.runs.length === 0) && <div style={{ fontSize: 11, color: C.muted, padding: 20, textAlign: "center" }}>No evaluation runs yet</div>}
    </div>}

    {sub === "feedback" && <div>
      {fbStats && !fbStats.error && <div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          <Stat label="Total feedback" value={fbStats.total || 0} color={C.purple} />
          <Stat label="Accepted" value={fbStats.accepted || 0} color={C.emerald} />
          <Stat label="Rejected" value={fbStats.rejected || 0} color={C.red} />
          <Stat label="Edited" value={fbStats.edited || 0} color={C.amber} />
        </div>
        {fbStats.acceptance_rate !== undefined && <div>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Acceptance rate</div>
          <Bar pct={fbStats.acceptance_rate * 100} color={C.emerald} h={8} />
          <div style={{ fontSize: 10, color: C.emerald, marginTop: 2 }}>{(fbStats.acceptance_rate * 100).toFixed(1)}%</div>
        </div>}
      </div>}
      {(!fbStats || fbStats.error) && <div style={{ fontSize: 11, color: C.muted, padding: 20, textAlign: "center" }}>No feedback data yet — approve/reject AI drafts to build the RLHF dataset</div>}
    </div>}

    {sub === "export" && <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Btn onClick={exportTraining} disabled={loading === "export"} color={C.purple}>{loading === "export" ? "Exporting..." : "Export training pairs"}</Btn>
        <Btn onClick={exportGolden} disabled={loading === "golden-export"} color={C.emerald}>{loading === "golden-export" ? "Exporting..." : "Export golden dataset"}</Btn>
      </div>
      {trainingData && !trainingData.error && <div style={{ padding: 12, borderRadius: 8, background: C.card, border: `1px solid ${C.purple}33` }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <Stat label="Total pairs" value={trainingData.total || trainingData.dataset?.length || 0} color={C.purple} />
          <Stat label="Format" value={trainingData.format || "jsonl"} color={C.cyan} />
        </div>
        <div style={{ fontSize: 10, color: C.muted, maxHeight: 200, overflow: "auto", fontFamily: "monospace", padding: 8, background: C.surface, borderRadius: 4 }}>
          {JSON.stringify(trainingData.dataset?.slice(0, 3) || trainingData.pairs?.slice(0, 3), null, 2)?.substring(0, 1000)}
        </div>
      </div>}
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════
// TAB 16: REPORTS
// ═══════════════════════════════════════════════════════════
function ReportsTab() {
  return <div data-testid="reports-tab-content" style={{ padding: 20 }}>
    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: C.text }}>Product Readiness Report</div>
    <div style={{ padding: 16, borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12 }}>Download Report</div>
      <div style={{ display: "flex", gap: 10 }}>
        <a data-testid="download-excel-btn" href="/downloads/DisclosureOS_Product_Readiness_Report.xlsx" download style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${C.emerald}`, background: C.emerald + "15", color: C.emerald, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>Excel Report (.xlsx)</a>
        <a data-testid="download-markdown-btn" href="/downloads/DisclosureOS_Product_Readiness_Report.md" download style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${C.blue}`, background: C.blue + "15", color: C.blue, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>Markdown Report (.md)</a>
      </div>
    </div>
    <div style={{ fontSize: 10, color: C.muted }}>Report contains 7 sheets: Executive Summary, Architecture Gaps, AI Tasks, Data Model, RAGAS Evaluation, Readiness Scorecard, Infrastructure</div>
  </div>;
}


// MAIN APP
// ═══════════════════════════════════════════════════════════
export default function DisclosureOS() {
  const [tab, setTab] = useState("entity");
  const tabs = [
    { id: "entity", label: "Entity" },
    { id: "iro", label: "IRO Matrix" },
    { id: "requirements", label: "Requirements" },
    { id: "evidence", label: "Evidence" },
    { id: "ai", label: "AI Drafts" },
    { id: "agents", label: "AI Agents" },
    { id: "graphrag", label: "GraphRAG" },
    { id: "evaluation", label: "Evaluation" },
    { id: "observability", label: "Observability" },
    { id: "enterprise", label: "Enterprise" },
    { id: "events", label: "Events" },
    { id: "eventbus", label: "Event Bus" },
    { id: "lineage", label: "Lineage" },
    { id: "snapshots", label: "Snapshots" },
    { id: "search", label: "Search" },
    { id: "reports", label: "Reports" },
  ];

  return <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
    <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, background: C.bg + "f0", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: 5, background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#fff", flexShrink: 0 }}>D</div>
        <div style={{ marginRight: 8 }}><div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>DisclosureOS</div><div style={{ fontSize: 7, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>v6.0 — 203 endpoints, 31 tables, 6 AI agents</div></div>
        <div style={{ display: "flex", gap: 2, flexWrap: "wrap", flex: 1 }}>
          {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "3px 8px", borderRadius: 4, border: `1px solid ${tab === t.id ? C.blue : "transparent"}`, background: tab === t.id ? C.blue + "18" : "transparent", color: tab === t.id ? C.blue : C.muted, fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>{t.label}</button>)}
        </div>
      </div>
    </div>
    <div style={{ padding: "16px 20px 40px", maxWidth: 960, margin: "0 auto" }}>
      {tab === "entity" && <EntityTab />}
      {tab === "iro" && <IROTab />}
      {tab === "requirements" && <RequirementsTab />}
      {tab === "evidence" && <EvidenceTab />}
      {tab === "ai" && <AITab />}
      {tab === "agents" && <AIAgentsTab />}
      {tab === "graphrag" && <GraphRAGTab />}
      {tab === "evaluation" && <EvaluationTab />}
      {tab === "observability" && <ObservabilityTab />}
      {tab === "enterprise" && <EnterpriseTab />}
      {tab === "events" && <EventsTab />}
      {tab === "eventbus" && <EventBusTab />}
      {tab === "lineage" && <LineageTab />}
      {tab === "snapshots" && <SnapshotsTab />}
      {tab === "search" && <SearchTab />}
      {tab === "reports" && <ReportsTab />}
    </div>
    <div style={{ textAlign: "center", padding: "12px 0 24px", fontSize: 10, color: C.dim }}>"Enter data once. <span style={{ color: C.emerald, fontWeight: 600 }}>Defend it everywhere.</span>"</div>
  </div>;
}
